import { useNavigate } from "react-router-dom";
import { Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInterpreterVoice } from "@/hooks/useInterpreterVoice";
import {
  INTERPRETER_NOTICE,
  interpreterStatusLabel,
  speakerLabel,
  stagePathForSlug,
  visibleInterpreterLines,
  type InterpreterSpeaker,
} from "@/lib/interpreter";

const PTT: Array<{ speaker: InterpreterSpeaker; label: string }> = [
  { speaker: "you", label: "Eu falo" },
  { speaker: "them", label: "O outro fala" },
];

const Ajuda = () => {
  const navigate = useNavigate();
  const { status, transcripts, holding, error, ended, toggle, endSession } = useInterpreterVoice();
  const lines = visibleInterpreterLines(transcripts);
  const live = status !== "idle" || lines.length > 0;

  const handleEnd = async () => {
    try {
      await endSession();
    } catch {
      // a tela de encerrar ainda aparece com o match local
    }
  };

  if (ended) {
    return (
      <div className="flex min-h-screen flex-col bg-neutral-50 px-5 pb-10 pt-6 text-neutral-900 md:px-8">
        <header className="mx-auto flex w-full max-w-lg items-center justify-between">
          <p className="text-[15px] text-neutral-500">Ajuda ao vivo</p>
          <button
            type="button"
            onClick={() => navigate("/app")}
            aria-label="Fechar"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white text-xl leading-none text-neutral-500"
          >
            ×
          </button>
        </header>
        <main className="mx-auto mt-8 w-full max-w-lg">
          <h1 className="text-[1.65rem] font-extrabold tracking-[-0.03em]">O aperto ficou gravado</h1>
          <p className="mt-2 text-[15px] text-neutral-500">
            Quer ensaiar isso no palco? Sem correção — só repetir a situação.
          </p>
          {lines.length > 0 && (
            <div className="mt-6 space-y-4">
              {lines.map((line, index) => (
                <div key={`${line.speaker}-${line.role}-${index}`}>
                  <p className="text-[13px] text-neutral-500">{speakerLabel(line.speaker)}</p>
                  <p className="mt-1 text-[17px] font-medium leading-6">{line.text}</p>
                </div>
              ))}
            </div>
          )}
          <Button
            className="mt-8 w-full"
            onClick={() => navigate(stagePathForSlug(ended.matchedSlug))}
          >
            Ensaiar no palco
          </Button>
          <button
            type="button"
            className="mt-4 w-full text-center text-[15px] text-neutral-500"
            onClick={() => navigate("/app")}
          >
            Voltar ao início
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 px-5 pb-10 pt-4 text-neutral-900 md:px-8">
      <header className="mx-auto flex w-full max-w-lg items-center justify-between pt-1">
        <p className="text-[15px] text-neutral-500">Ajuda ao vivo</p>
        <button
          type="button"
          onClick={() => void handleEnd()}
          aria-label="Encerrar ajuda"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white text-xl leading-none text-neutral-500"
        >
          ×
        </button>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col px-2 pt-8">
        <div className="flex flex-col items-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#DBEAFE]">
            <Volume2 className="h-8 w-8 text-blue-600" strokeWidth={1.75} aria-hidden />
          </div>
          <p className="mt-4 text-center text-[15px] leading-6 text-neutral-500">{INTERPRETER_NOTICE}</p>
        </div>

        <div className="mt-8 flex-1">
          <p className="text-center text-[17px] text-neutral-500">
            {interpreterStatusLabel(status, holding !== null)}
          </p>
          {lines.length > 0 ? (
            <div className="mt-6 space-y-5">
              {lines.map((line, index) => (
                <div key={`${line.speaker}-${line.role}-${index}`}>
                  <p className="text-center text-[13px] text-neutral-500">{speakerLabel(line.speaker)}</p>
                  <p className="mt-1 text-center text-[18px] font-medium leading-7 tracking-[-0.02em]">
                    {line.text}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
          {error ? <p className="mt-4 text-center text-sm text-orange-700">{error}</p> : null}
        </div>
      </main>

      <footer className="mx-auto grid w-full max-w-lg grid-cols-2 gap-3 pb-4 pt-6">
        {PTT.map((item) => {
          const active = holding === item.speaker;
          return (
            <button
              key={item.speaker}
              type="button"
              disabled={status === "connecting"}
              aria-label={item.label}
              aria-pressed={active}
              onClick={() => {
                void toggle(item.speaker);
              }}
              className={`h-20 rounded-2xl text-base font-semibold text-white shadow-[0_10px_24px_rgba(15,23,42,0.12)] disabled:opacity-60 ${
                active ? "bg-blue-500" : "bg-neutral-900"
              }`}
            >
              {item.label}
            </button>
          );
        })}
        {live ? (
          <button
            type="button"
            onClick={() => void handleEnd()}
            className="col-span-2 mt-1 text-center text-[15px] text-neutral-500"
          >
            Encerrar
          </button>
        ) : null}
      </footer>
    </div>
  );
};

export default Ajuda;
